/**
 * AnswerForm -- Inline form for answering a checklist question
 *
 * Renders the appropriate input control based on question type:
 * - yes_no: Radix Switch with compliance labels
 * - date: DatePicker (compound, from UI library)
 * - text: Textarea (auto-resize)
 * - document: Input with placeholder
 * - selection: Textarea fallback (options TBD)
 *
 * Shows hint + expectedEvidence in a collapsible details section.
 * Footer has Cancel and Save Answer buttons.
 */

import { useState, useCallback } from "react";
import * as Popover from "@radix-ui/react-popover";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import type { GeneratedQuestion } from "@/packages/types";
import { Button, Input, Textarea, DatePicker } from "@/components/ui";

// ─── Props ──────────────────────────────────────────────────────────────────

interface AnswerFormProps {
  question: GeneratedQuestion;
  initialValue?: string | boolean | number;
  onSave: (value: string | boolean | number) => void;
  onCancel: () => void;
  isSaving: boolean;
}

// ─── Default Values ─────────────────────────────────────────────────────────

function getDefaultValue(
  type: GeneratedQuestion["type"],
): string | boolean | number {
  switch (type) {
    case "yes_no":
      return false;
    case "date":
      return Date.now();
    case "text":
    case "document":
    case "selection":
      return "";
  }
}

// ─── Yes/No Toggle ──────────────────────────────────────────────────────────

function YesNoInput({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <SwitchPrimitive.Root
        checked={value}
        onCheckedChange={onChange}
        className="relative h-[22px] w-[40px] cursor-pointer rounded-full bg-muted/30 transition-colors data-[state=checked]:bg-green-500"
      >
        <SwitchPrimitive.Thumb className="block h-[18px] w-[18px] translate-x-0.5 rounded-full bg-white shadow-sm transition-transform data-[state=checked]:translate-x-[20px]" />
      </SwitchPrimitive.Root>
      <span className="text-[13px] font-medium text-primary">
        {value ? "Yes (Compliant)" : "No (Non-compliant)"}
      </span>
    </div>
  );
}

// ─── Date Input ─────────────────────────────────────────────────────────────

function DateInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  const dateValue =
    value && !Number.isNaN(value) ? new Date(value) : new Date();

  const handleChange = useCallback(
    (date: Date | undefined) => {
      if (date) {
        onChange(date.getTime());
      }
    },
    [onChange],
  );

  return (
    <DatePicker value={dateValue} onChange={handleChange} autoConfirm>
      <DatePicker.Trigger variant="input" />
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          className="z-50 rounded-[12px] border border-border bg-card shadow-lg"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DatePicker.Calendar />
        </Popover.Content>
      </Popover.Portal>
    </DatePicker>
  );
}

// ─── Hint Section ───────────────────────────────────────────────────────────

function HintSection({ question }: { question: GeneratedQuestion }) {
  if (!question.hint && !question.expectedEvidence) {
    return null;
  }

  return (
    <details className="mt-2">
      <summary className="text-[11px] text-muted cursor-pointer select-none">
        Hint
      </summary>
      {question.hint && (
        <p className="text-[12px] text-secondary mt-1">{question.hint}</p>
      )}
      {question.expectedEvidence && (
        <p className="text-[11px] text-muted mt-0.5">
          Expected evidence: {question.expectedEvidence}
        </p>
      )}
    </details>
  );
}

// ─── Main Export ─────────────────────────────────────────────────────────────

export function AnswerForm({
  question,
  initialValue,
  onSave,
  onCancel,
  isSaving,
}: AnswerFormProps) {
  const [value, setValue] = useState<string | boolean | number>(
    initialValue ?? getDefaultValue(question.type),
  );

  return (
    <div className="rounded-lg border border-accent/20 bg-accent/5 px-3.5 py-3 space-y-3">
      {/* Input control per type */}
      {question.type === "yes_no" && (
        <YesNoInput
          value={typeof value === "boolean" ? value : value === "true"}
          onChange={(v) => setValue(v)}
        />
      )}

      {question.type === "date" && (
        <DateInput
          value={
            typeof value === "number" ? value : Number(value) || Date.now()
          }
          onChange={(v) => setValue(v)}
        />
      )}

      {question.type === "text" && (
        <Textarea
          value={String(value)}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Enter your answer..."
          className="min-h-[80px]"
        />
      )}

      {question.type === "document" && (
        <Input
          value={String(value)}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Enter document name or reference"
        />
      )}

      {question.type === "selection" && (
        <Textarea
          value={String(value)}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Enter your selection..."
          className="min-h-[80px]"
        />
      )}

      {/* Hint / expected evidence */}
      <HintSection question={question} />

      {/* Footer buttons */}
      <div className="flex justify-end gap-2 mt-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => onSave(value)}
          disabled={isSaving}
          loading={isSaving}
        >
          Save Answer
        </Button>
      </div>
    </div>
  );
}
